import { Box, Button, styled, Typography, type Theme } from '@mui/material';

export const TableButton = styled(Button)(({ theme }) => ({
  padding: '6.5px 10px',
  borderRadius: '6px',
  border: `1px solid ${theme.palette.grey[100]}`,

  ' .MuiButton-startIcon': {
    marginRight: '5px',
  },

  '&:hover': {
    border: `1px solid ${theme.palette.grey[100]}`,
  },
}));

export const MessageText = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  lineHeight: '24px',

  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    lineHeight: '21px',
  },
}));

export const MessagesContainer = styled(Box)(({ theme }) => ({
  padding: '40px 20px 40px 30px',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  // Su mobile la chat deve restare nel viewport; le tabelle scrollano nel proprio wrapper.
  overflowX: 'hidden',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',

  [theme.breakpoints.down('sm')]: {
    padding: '16px 12px',
  },
}));

export const Wrapper = styled(Box)(({ theme }) => ({
  paddingLeft: '240px',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',

  '& > *': {
    minWidth: 0,
  },

  [theme.breakpoints.down('md')]: {
    paddingLeft: '220px',
  },

  [theme.breakpoints.down('sm')]: {
    paddingLeft: 0,
    position: 'fixed',
    top: 'calc(60px + var(--kipify-topbar-height, 0px))',
    left: 0,
    right: 0,
    width: '100%',
    maxWidth: '100vw',
    height: 'calc(100dvh - 60px - var(--kipify-topbar-height, 0px))',
    overflow: 'hidden',
  },
}));

export const MessageActionButton = styled(Button)(({ theme }) => ({
  minWidth: '40px',
  height: '40px',
  borderRadius: '6px',
  backgroundColor: theme.palette.grey[200],

  '&:hover': {
    backgroundColor: theme.palette.grey[200],
  },
}));

export const AnswerMessageWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '15px',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.grey[200]}`,
  padding: '10px',
  alignItems: 'flex-start',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',

  [theme.breakpoints.down('sm')]: {
    gap: '10px',
    padding: '8px',
  },
}));

export const ResLetterAvatarWrapper = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',
  height: '40px',
  minWidth: '40px',
  borderRadius: '6px',
  backgroundColor: theme.palette.primary.main,

  [theme.breakpoints.down('sm')]: {
    height: '36px',
    minWidth: '36px',
  },
}));

export const InputWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}));

/** Chat composer: single border (theme green on focus) with textarea + toolbar inside. */
export const ChatInputShell = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active?: boolean }>(({ theme, $active }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '6px',
  border: `1px solid ${$active ? theme.palette.primary.main : theme.palette.grey[100]}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  ...($active && {
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  }),
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  '.MuiInputBase-root': {
    padding: 0,
    fontSize: '16px',
    lineHeight: '22px',
  },
  '.MuiInputBase-inputMultiline': {
    padding: '15px 15px 6px',
    minHeight: '22px',
  },
  '&::before, &::after': {
    display: 'none',
  },
}));

export const ChatInputToolbar = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '8px 10px 10px',
}));

export const ChatSkillsTrigger = styled('button', {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active?: boolean }>(({ theme, $active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  margin: 0,
  padding: '6px 10px',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '18px',
  color: theme.palette.text.primary,
  backgroundColor: $active
    ? theme.palette.primary.light
    : theme.palette.grey[200],
  transition: 'background-color 0.15s ease',

  '&:hover': {
    backgroundColor: $active
      ? theme.palette.primary.light
      : theme.palette.grey[100],
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },

  '& svg': {
    width: 16,
    height: 16,
    flexShrink: 0,
    color: $active
      ? theme.palette.primary.contrastText
      : theme.palette.text.secondary,
  },
}));

export const LetterAvatarWrapper = styled(Box)(({ theme }) => ({
  height: '40px',
  minWidth: '40px',
  borderRadius: '6px',
  backgroundColor: theme.palette.primary.contrastText,
  alignItems: 'center',
  justifyContent: 'center',
  display: 'flex',

  [theme.breakpoints.down('sm')]: {
    height: '36px',
    minWidth: '36px',
  },
}));

export const MessageWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '15px',
  borderRadius: '12px',
  backgroundColor: theme.palette.primary.light,
  alignItems: 'center',
  padding: '10px',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',

  [theme.breakpoints.down('sm')]: {
    gap: '10px',
    padding: '8px',
  },
}));

export const PreviewWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: '40px',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  justifyContent: 'center',
  gap: '10px',
  overflowY: 'auto',
  overflowX: 'hidden',

  ' .description': {
    color: theme.palette.grey[800],
  },

  [theme.breakpoints.down('sm')]: {
    padding: '20px',

    ' .label': {
      marginTop: '-5px',
    },

    ' .title': {
      fontSize: '40px',
      lineHeight: '52px',
    },

    ' .description': {
      fontSize: '20px',
      lineHeight: '26px',
    },
  },
}));

export const PreviewText = styled(Typography)(({ theme }) => ({
  fontSize: '18px',
  lineHeight: '24px',
  fontWeight: 400,

  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    lineHeight: '21px',
  },
}));

export const PreviewPromtButton = styled(Box)(({ theme }) => ({
  padding: '14px',
  backgroundColor: theme.palette.grey[200],
  borderRadius: '6px',
  textAlign: 'center',
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

export const PreviewTextBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '15px',
  marginTop: '20px',
  flexDirection: 'column',
  width: '100%',

  [theme.breakpoints.down('sm')]: {
    marginTop: '10px',
  },
}));

export const InputContainer = styled(Box)(({ theme }) => ({
  padding: '25px 30px',
  borderTop: `1px solid ${theme.palette.grey[100]}`,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  flexShrink: 0,

  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

export const GdprBanner = styled(Box)(({ theme }) => ({
  marginTop: '8px',
  textAlign: 'center',

  '& .MuiTypography-root': {
    fontSize: '14px',
    lineHeight: '18px',
    color: theme.palette.grey[600],
  },
}));

/** Testo in arrivo (streaming): piccolo e grigio; a fine stream si usa MarkdownBody con design normale */
export const StreamingMessageText = styled('div')(({ theme }) => ({
  fontSize: '13px',
  lineHeight: '1.5',
  color: theme.palette.grey[600],
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

/** Scroll orizzontale per tabelle GFM dentro i messaggi (campagne lunghe, molte colonne). */
export const MarkdownTableWrapper = styled('div')(({ theme }) => ({
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  margin: '16px 0',
  WebkitOverflowScrolling: 'touch',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.background.paper,
  // Barra scroll sempre utilizzabile su trackpad / touch
  overscrollBehaviorX: 'contain',
  touchAction: 'pan-x pan-y',

  // La tabella interna non duplica il bordo esterno
  '& table': {
    margin: 0,
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'auto',
  },

  [theme.breakpoints.down('sm')]: {
    margin: '12px 0',
    borderRadius: '6px',
    '& table': {
      fontSize: '13px',
    },
    '& th, & td': {
      padding: '8px 10px',
    },
  },
  // Immagini nelle tabelle: trattale sempre come thumbnail (non “hero”).
  '& table img': {
    width: '56px !important',
    height: '56px !important',
    maxWidth: '56px !important',
    maxHeight: '56px !important',
    objectFit: 'cover',
    borderRadius: '8px',
    display: 'block',
    margin: 0,
    boxShadow: 'none',
  },
}));

function markdownTableStyles(theme: Theme) {
  return {
    '& table': {
      width: 'max-content',
      minWidth: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: '14px',
      lineHeight: 1.45,
    },
    '& thead tr': {
      backgroundColor: theme.palette.grey[100],
    },
    '& th': {
      fontWeight: 600,
      textAlign: 'left' as const,
      padding: '10px 12px',
      borderBottom: `2px solid ${theme.palette.grey[300]}`,
      color: theme.palette.grey[800],
      whiteSpace: 'nowrap' as const,
    },
    '& td': {
      padding: '10px 12px',
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
      verticalAlign: 'top' as const,
      color: theme.palette.grey[900],
    },
    '& tbody tr:last-of-type td': {
      borderBottom: 'none',
    },
    '& tbody tr:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  };
}

/** Markdown in stream: struttura GFM + palette remota (13px, grey[600]). */
export const StreamingMarkdownBody = styled('div')(({ theme }) => ({
  minWidth: 0,
  maxWidth: '100%',
  overflowX: 'hidden',
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  fontSize: '13px',
  lineHeight: '1.5',
  color: theme.palette.grey[600],

  '& p': {
    margin: '8px 0',
    color: theme.palette.grey[600],
  },
  '& li': {
    margin: '6px 0',
    color: theme.palette.grey[600],
  },
  // paddingLeft + inside: con overflowX:hidden i marker outside venivano tagliati (es. "16.")
  '& ul, & ol': {
    margin: '8px 0',
    paddingLeft: '1.25em',
    listStylePosition: 'inside',
  },
  '& h1, & h2, & h3, & h4': {
    margin: '12px 0 6px',
    fontWeight: 600,
    lineHeight: 1.35,
    color: theme.palette.grey[700],
  },
  '& h2': { fontSize: '1.08em' },
  '& h3': { fontSize: '1.02em' },
  '& strong': {
    fontWeight: 600,
    color: theme.palette.grey[700],
  },
  ...markdownTableStyles(theme),
  '& th': {
    fontSize: '13px',
    color: theme.palette.grey[700],
  },
  '& td': {
    fontSize: '13px',
    color: theme.palette.grey[600],
  },
}));

/** Memo Discount Analysis: liste annidate leggibili + righe Scope/Why/Check separate. */
export const ClosingDecisionMemoMarkdownBody = styled('div')(({ theme }) => ({
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  fontSize: '16px',
  lineHeight: '1.65',
  color: theme.palette.grey[900],
  minWidth: 0,
  maxWidth: '100%',
  overflowX: 'hidden',

  '& p': {
    margin: '10px 0',
    lineHeight: '1.65',
  },

  '& ol': {
    margin: '16px 0',
    paddingLeft: '1.25em',
    listStylePosition: 'inside',
  },

  '& ol > li': {
    margin: '20px 0 24px',
    paddingLeft: 0,
    '&::before': {
      display: 'none',
    },
  },

  '& ol > li > ul': {
    margin: '10px 0 0',
    paddingLeft: '8px',
    listStyleType: 'none',
  },

  '& ol > li > ul > li': {
    margin: '10px 0',
    paddingLeft: '4px',
    lineHeight: '1.6',
    '&::before': {
      display: 'none',
    },
  },

  '& ol > li > ul > li > p': {
    display: 'block',
    margin: '0',
  },

  '& ul': {
    margin: '12px 0',
    paddingLeft: '24px',
    listStyleType: 'none',
  },

  '& ul > li': {
    margin: '12px 0',
    position: 'relative',
    paddingLeft: '0',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-20px',
      top: '10px',
      width: '7px',
      height: '7px',
      borderRadius: '50%',
      backgroundColor: theme.palette.warning.main,
      opacity: 0.85,
    },
  },

  '& ul > li > p': {
    display: 'block',
    margin: '0',
  },

  '& h2, & h3': {
    margin: '20px 0 10px',
    fontWeight: 700,
  },

  '& strong': {
    fontWeight: 700,
  },

  ...markdownTableStyles(theme),
}));

// Ottimizzato per leggibilità e UX professionale
export const MarkdownBody = styled('div')(({ theme }) => ({
  wordWrap: 'break-word',
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  fontSize: '16px',
  lineHeight: '1.7',
  color: theme.palette.grey[900],
  // Necessario in flex layout: altrimenti la tabella “spinge” e non compare scroll interno
  minWidth: 0,
  maxWidth: '100%',
  overflowX: 'hidden',

  '& p': {
    margin: '12px 0',
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    lineHeight: '1.7',
  },

  // Liste flat - bullet points più discreti
  '& ul': {
    margin: '12px 0',
    paddingLeft: '24px',
    listStyleType: 'none', // Rimuoviamo i bullet default
  },

  // Liste numerate: list-style inside evita che overflowX:hidden tagli "16."/"20."
  '& ol': {
    margin: '12px 0',
    paddingLeft: '0.5em',
    listStylePosition: 'inside',
  },

  '& ol li': {
    margin: '12px 0',
    lineHeight: '1.7',
    paddingLeft: 0,

    // Rimuovi bullet per liste numerate
    '&::before': {
      display: 'none',
    },
  },

  '& li': {
    margin: '10px 0',
    lineHeight: '1.7',
    position: 'relative',
    paddingLeft: '0',

    // Bullet primo livello - cerchio più grande e colorato (città/categorie/prodotti)
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-20px',
      top: '10px',
      width: '7px',
      height: '7px',
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      opacity: 0.7,
    },
  },

  // Rimuovi margini extra da paragrafi dentro liste
  '& li > p': {
    margin: '0',
    display: 'inline',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },

  // Nested lists - secondo livello (valori)
  '& ul ul': {
    margin: '6px 0 6px 0',
    paddingLeft: '24px',
  },

  // Bullet secondo livello - cerchio vuoto più piccolo (valori come Revenue, ROAS, etc.)
  '& ul ul li::before': {
    width: '6px',
    height: '6px',
    backgroundColor: 'transparent',
    border: `1.5px solid ${theme.palette.grey[400]}`,
    borderRadius: '50%',
    top: '11px',
  },

  // Headers - gerarchia visiva chiara
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '24px 0 12px',
    fontWeight: 600,
    lineHeight: '1.3',
    color: theme.palette.grey[900],
  },

  '& h2': {
    fontSize: '1.5em',
    marginTop: '28px',
    marginBottom: '14px',
    fontWeight: 700,
  },

  '& h3': {
    fontSize: '1.2em',
    marginTop: '20px',
  },

  '& h4': {
    fontSize: '1.05em',
    marginTop: '16px',
    marginBottom: '8px',
    color: theme.palette.grey[800],
  },

  '& hr': {
    margin: '20px 0',
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
  },

  // Bold text - peso ridotto per migliore leggibilità
  '& strong': {
    fontWeight: 600,
    color: theme.palette.grey[900],
  },

  // Prima lista sotto un header - margine ridotto
  '& h1 + ul, & h2 + ul, & h3 + ul': {
    marginTop: '8px',
  },

  // Immagini markdown (screenshot, asset generici): le card creative Meta usano `.kipify-chat-creative-card`
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[2],
    display: 'block',
    margin: '12px 0',
  },

  '& .kipify-chat-creative-thumb': {
    margin: '8px 0',
  },

  // Spacing extra tra gruppi di contenuto
  '& > *:not(:last-child)': {
    marginBottom: '8px',
  },

  ...markdownTableStyles(theme),

  [theme.breakpoints.down('sm')]: {
    fontSize: '15px',
    lineHeight: '1.6',

    '& li > p': {
      display: 'block',
    },

    '& h2': {
      fontSize: '1.28em',
    },

    '& h3': {
      fontSize: '1.1em',
    },
  },
}));
